import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Space, Spin, message } from 'antd';
import { PlusOutlined, SearchOutlined, DownOutlined, FilterOutlined, CloseOutlined, SortAscendingOutlined, InboxOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { watchesAPI } from '../../../Utils/api';
import { copyText } from '../../../Utils/clipboard';
import ProductCard from './ProductCard';
import ProductFilterSheet from './ProductFilterSheet';
import ProductSortSheet, { SORT_OPTIONS } from './ProductSortSheet';
import ProductPreviewSheet from './ProductPreviewSheet';
import { MobileWatch, STATUS_LABELS, formatPrice } from './productStatus';
import { Header, CountRow, List, LoadMore, Toolbar, FilterRow, Chip, EmptyState } from './ProductMobileList.style';

const FE_URL = process.env.REACT_APP_FE_URL || 'https://www.mr-luxury-watches.com';

interface ProductMobileListProps {
  watches: MobileWatch[];
  totalCount: number;
  loading: boolean;
  onLoadMore: () => void;
  onDeleted: (id: string) => void;
  filters: Record<string, string>;
  onFiltersChange: (filters: Record<string, string>) => void;
  sortField: string;
  sortOrder: 'ASC' | 'DESC';
  onSortChange: (field: string, order: 'ASC' | 'DESC') => void;
  exportButton?: React.ReactNode;
}

const CHIP_LABELS: Record<string, string> = { model: 'Modell', sku: 'SKU', referenceNumber: 'Ref.' };

const ProductMobileList: React.FC<ProductMobileListProps> = ({
  watches, totalCount, loading, onLoadMore, onDeleted, filters, onFiltersChange, sortField, sortOrder, onSortChange, exportButton,
}) => {
  const navigate = useNavigate();
  const [modal, modalContext] = Modal.useModal();
  const [search, setSearch] = useState(filters.search || '');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [preview, setPreview] = useState<MobileWatch | null>(null);

  useEffect(() => {
    setSearch(filters.search || '');
  }, [filters.search]);

  // Debounce typing so the list is not reloaded on every key stroke
  useEffect(() => {
    if (search.trim() === (filters.search || '')) return undefined;
    const timer = window.setTimeout(() => updateFilter('search', search.trim()), 400);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const updateFilter = (key: string, value?: string) => {
    const next = { ...filters };
    if (value) next[key] = value; else delete next[key];
    onFiltersChange(next);
  };

  const priceLabel = () => {
    const min = filters.priceMin ? formatPrice(Number(filters.priceMin)) : null;
    const max = filters.priceMax ? formatPrice(Number(filters.priceMax)) : null;
    return min && max ? `${min} – ${max}` : min ? `ab ${min}` : `bis ${max}`;
  };

  const chip = (key: string, label: string, onRemove = () => updateFilter(key)) => (
    <Chip key={key} type="button" onClick={onRemove} data-testid={`mobile-chip-${key}`}>
      <span>{label}</span>
      <CloseOutlined />
    </Chip>
  );

  const sortLabel = SORT_OPTIONS.find((o) => o.field === sortField && o.order === sortOrder)?.label || 'Sortieren';

  const edit = (watch: MobileWatch) => navigate(`/products/${watch.id}`);

  const copyShortLink = async (watch: MobileWatch) => {
    if (!watch.shortCode) return;
    if (await copyText(`${FE_URL}/s/${watch.shortCode}`)) {
      message.success('Short-Link kopiert');
    } else {
      message.error('Kopieren nicht möglich');
    }
  };

  const confirmDelete = (watch: MobileWatch) => {
    modal.confirm({
      title: 'Produkt löschen',
      content: `Soll "${watch.brand} ${watch.model}" wirklich gelöscht werden?`,
      okText: 'Ja, löschen',
      cancelText: 'Abbrechen',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await watchesAPI.deleteWatch(watch.id);
          message.success('Produkt gelöscht');
          setPreview(null);
          onDeleted(watch.id);
        } catch (e: any) {
          message.error(e?.response?.data?.message || 'Löschen fehlgeschlagen');
        }
      },
    });
  };

  return (
    <div>
      {modalContext}
      <Header>
        <h1>Produkte</h1>
        <Space>
          {exportButton}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/products/new')}>
            Neues Produkt
          </Button>
        </Space>
      </Header>
      <Toolbar>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Produkte suchen …"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="mobile-search"
        />
        <FilterRow>
          <Button icon={<FilterOutlined />} onClick={() => setFilterOpen(true)} data-testid="mobile-filter-open">Filter</Button>
          {filters.brand && chip('brand', filters.brand)}
          {filters.status && chip('status', STATUS_LABELS[filters.status] || filters.status)}
          {Object.keys(CHIP_LABELS).filter((key) => filters[key]).map((key) => chip(key, `${CHIP_LABELS[key]}: ${filters[key]}`))}
          {(filters.priceMin || filters.priceMax) && chip('price', priceLabel(), () => {
            const next = { ...filters };
            delete next.priceMin;
            delete next.priceMax;
            onFiltersChange(next);
          })}
        </FilterRow>
      </Toolbar>
      <CountRow>
        <span data-testid="mobile-watch-count">{totalCount} {totalCount === 1 ? 'Produkt' : 'Produkte'}</span>
        <Button type="text" icon={<SortAscendingOutlined />} onClick={() => setSortOpen(true)} data-testid="mobile-sort-open">
          {sortLabel} <DownOutlined />
        </Button>
      </CountRow>
      <List>
        {watches.map((watch) => (
          <ProductCard
            key={watch.id}
            watch={watch}
            onOpen={setPreview}
            onEdit={edit}
            onCopyShortLink={copyShortLink}
            onDelete={confirmDelete}
          />
        ))}
      </List>
      {!loading && watches.length === 0 && (
        <EmptyState data-testid="mobile-empty-state">
          <InboxOutlined />
          <h3>Keine Produkte gefunden</h3>
          {Object.keys(filters).length > 0 ? (
            <>
              <span>Keine Produkte entsprechen den aktuellen Filtern.</span>
              <Button icon={<ReloadOutlined />} onClick={() => onFiltersChange({})} data-testid="mobile-empty-reset">Filter zurücksetzen</Button>
            </>
          ) : (
            <span>Lege über „Neues Produkt“ die erste Uhr an.</span>
          )}
        </EmptyState>
      )}
      {loading && <LoadMore><Spin /></LoadMore>}
      {!loading && watches.length < totalCount && (
        <LoadMore>
          <Button onClick={onLoadMore} data-testid="mobile-load-more">Mehr laden</Button>
        </LoadMore>
      )}
      <ProductFilterSheet
        open={filterOpen}
        filters={filters}
        onClose={() => setFilterOpen(false)}
        onApply={(next) => { onFiltersChange(next); setFilterOpen(false); }}
      />
      <ProductPreviewSheet
        watch={preview}
        onClose={() => setPreview(null)}
        onEdit={edit}
        onDelete={confirmDelete}
        onCopyShortLink={copyShortLink}
      />
      <ProductSortSheet
        open={sortOpen}
        sortField={sortField}
        sortOrder={sortOrder}
        onClose={() => setSortOpen(false)}
        onChange={(field, order) => { onSortChange(field, order); setSortOpen(false); }}
      />
    </div>
  );
};

export default ProductMobileList;
